<template>
  <div>
    <div
      v-if="!isLoading && preparedContent && preparedContent.length"
      class="lpsection"
    >
      <div class="lpsection__group-wrapper">
        <div class="lpsection__group-details">
          <transition name="fade" mode="out-in">
            <div :key="currentGroup.name" class="lpsection__copy-wrapper">
              <p class="lpsection__group-section">
                Section {{ currentGroup.section }}
              </p>
              <p class="lpsection__group-name">
                {{ getTruncatedName(currentGroup.name) }}
              </p>
              <p class="lpsection__group-progress">
                {{ getPercentage(groupProgress[currentGroup.name]) }}% Complete
              </p>
            </div>
          </transition>
        </div>
        <Swiper
          v-if="preparedContent.length > 4"
          :class="[
            'lpsection__courses',
            !isFirstSlideActive && 'lpsection__courses--shadow',
          ]"
          :parentClass="`lpsection__swiper-sectional-groups`"
          :swiperOptions="swiperOptions"
          @slide-changed="handleSlideChange"
          @first-slide="handleSlideChange"
        >
          <div
            class="lpsection__course swiper-slide"
            v-for="content in preparedContent"
            :key="content.uuid"
          >
            <CourseTile
              :course="{
                ...content,
                id: content.uuid,
                learning_areas: [{ name: learningArea }],
                vendor: { name: vendor },
              }"
              :isFreeTrialUser="false"
              :isResumeSnipeShown="targetCourseId === content.uuid"
              :height="tileHeight"
            />
            <div
              v-if="targetCourseId === content.uuid"
              class="lpsection__course-glow"
            ></div>
          </div>
        </Swiper>
        <div v-else class="lpsection__courses-fallback">
          <div
            class="lpsection__course lpsection__course--fallback"
            v-for="content in preparedContent"
            :key="content.uuid"
          >
            <CourseTile
              :course="{
                ...content,
                id: content.uuid,
                learning_areas: [{ name: learningArea }],
                vendor: { name: vendor },
              }"
              :isFreeTrialUser="false"
              :isResumeSnipeShown="targetCourseId === content.uuid"
              :height="tileHeight"
            />
            <div
              v-if="targetCourseId === content.uuid"
              class="lpsection__course-glow"
            ></div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isLoading" class="lpsection lpsection__loader">
      <RecentlyActiveSkel />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Swiper from '@/components/layout/Swiper.vue'
import RecentlyActiveSkel from '@/components/loaders/skeletons/home-page/RecentlyActiveSkel.vue'
import { getEntities, truncateText } from '@/helpers'
import CourseTile from '@/components/layout/CourseTile.vue'

export default {
  name: 'LPSection',
  components: {
    Swiper,
    CourseTile,
    RecentlyActiveSkel,
  },
  props: {
    learningPathId: {
      type: String,
      required: true,
    },
    targetCourseId: {
      type: String,
      required: true,
    },
    isTablet: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      maxNameLength: 30,
      isLoading: true,
      currentCourseId: '',
      isFirstSlideActive: true,
      swiperOptions: {
        loop: false,
        mousewheel: {
          forceToAxis: true,
        },
        breakpointsBase: 'container',
        breakpoints: {
          0: {
            slidesPerView: 1.25,
            spaceBetween: 10,
          },
          500: {
            slidesPerView: 1.5,
            spaceBetween: 10,
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 10,
          },
          1024: {
            slidesPerView: 2,
            spaceBetween: 10,
          },
          1300: {
            slidesPerView: 3,
            spaceBetween: 10,
          },
        },
      },
    }
  },
  computed: {
    ...mapState({
      learningPath: state => state.content.recentLearningPath,
    }),
    learningArea() {
      if (this?.learningPath) {
        return getEntities.getLearningAreaName(this.learningPath)
      }
      return 'INE'
    },
    vendor() {
      if (this?.learningPath) {
        return getEntities.getVendorName(this.learningPath)
      }
      return 'INE'
    },
    preparedContent() {
      const content = []
      this.learningPath.content.map((group, i) => {
        group.content.map(c => {
          if (c.content_type && c.content_type === 'course') {
            content.push({ ...c, group: { name: group.name, section: i + 1 } })
          }
        })
      })
      return content
    },
    groupProgress() {
      const groups = {}
      this.learningPath.content.map(group => {
        groups[group.name] = {
          progress: 0,
          total: Math.floor(group.content.length * 100),
        }
        group.content.map(c => {
          const progress = c?.user_status?.progress ?? 0
          groups[group.name].progress += Math.floor(progress * 100)
        })
      })
      return groups
    },
    currentGroup() {
      const found = this.preparedContent.find(
        course => course.uuid === this.currentCourseId
      )
      if (found) {
        return found.group
      }
      return this.preparedContent[0].group
    },
    tileHeight() {
      return this.isTablet ? 171 : 245
    },
  },
  methods: {
    handleSlideChange(evt) {
      const index = evt?.activeIndex ?? 0
      this.isFirstSlideActive = index === 0
      this.currentCourseId = this.preparedContent[index].uuid
    },
    getPercentage(group) {
      const { progress, total } = group
      return Math.floor((progress / total) * 100)
    },
    getTruncatedName(name) {
      return truncateText(name, this.maxNameLength)
    },
  },
  async created() {
    this.isLoading = true
    await this.$store.dispatch(
      'content/getRecentLearningPath',
      this.learningPathId
    )
    this.isLoading = false
    this.handleSlideChange()
  },
}
</script>

<style lang="scss">
.lpsection {
  $this: &;
  padding: 5px 0 60px 0;
  position: relative;
  width: 100%;
  @include mobile {
    padding: 0 0 60px 0;
  }
  @include tablet {
    padding: 20px 0 171px 100px;
  }
  &__group-wrapper {
    display: flex;
    flex-wrap: wrap;
    @include tablet {
      flex-wrap: nowrap;
    }
  }
  &__group-details {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 16px;
    @include tablet {
      margin-bottom: 0;
      padding: 0;
      align-items: center;
      justify-content: center;
      min-width: 400px;
      width: 400px;
      &:first-child {
        min-width: 180px;
        width: 180px;
        margin-left: 25px;
      }
    }
    @include tablet {
      &:first-child {
        min-width: 237px;
        width: 237px;
        margin-left: 0;
      }
    }
  }
  &__copy-wrapper {
    z-index: 1;
    width: 100%;
    margin: 0 0 4px 0;
    padding-left: 25px;
    @include tablet {
      margin: 0;
      padding: 0 20px 0 0;
    }
  }
  &__group-section {
    color: var(--white);
    font-size: rem(12);
    line-height: 14px;
    margin: 0;
    padding: 0;
    width: 100%;
    transition: 0.2s all ease;
  }
  &__group-name {
    color: var(--white);
    font-size: rem(20);
    line-height: 26px;
    font-family: var(--font-bold);
    margin: 0;
    padding: 0;
    width: 100%;
    transition: 0.2s all ease;
  }
  &__group-progress {
    color: var(--positive-light);
    font-size: rem(14);
    line-height: 20px;
    padding: 0;
    display: flex;
    align-items: center;
    margin: 0;
  }
  &__progress-bar {
    margin: 0 10px 0 0;
  }
  &__courses {
    padding: 0 20px;
    mask-image: linear-gradient(to right, #030303 90%, transparent 100%);
    @include tablet {
      padding: 0;
    }
    &--shadow {
      mask-image: linear-gradient(
        to right,
        transparent 0%,
        #030303 10%,
        #030303 90%,
        transparent 100%
      );
    }
  }
  &__courses-fallback {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    padding: 0 0 0 25px;
    @include mobile {
      flex-wrap: nowrap;
    }
    @include desktop {
      padding: 0;
      width: 75%;
    }
  }
  &__course {
    position: relative;
    max-width: 280px;
    height: 171px;
    @include tablet {
      max-width: 400px;
      height: 245px;
    }
    &--fallback {
      margin: 0 10px 10px 0;
      width: 100%;
      @include mobile {
        margin: 0 10px 0 0;
      }
    }
  }
  &__course-glow {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    margin: 0 auto;
    height: 100%;
    width: 100%;
    opacity: 0.39;
    background: transparent
      radial-gradient(closest-side at 50% 50%, #ff00b0 0%, #261f7000 100%) 0% 0%
      no-repeat padding-box;
  }
  &__loader {
    max-height: 450px;
    margin: 0 0 100px 0;
    @include tablet {
      margin: 0 0 100px 117px;
      border-top-left-radius: 25px;
      border-bottom-left-radius: 25px;
    }
  }
  .swiper-button-prev,
  .swiper-button-next {
    height: 171px;
    @include tablet {
      height: 245px;
    }
  }
}
</style>
